/** Status de acesso do usuário no Hub, alterado pela ativação/inativação (RN01/RN03). */
export type UserStatus = "ativo" | "inativo";

export interface User {
  id: string;
  nome: string;
  email: string;
  /** Celular do diretório corporativo; vazio quando o serviço não informa. */
  celular: string;
  /** Time/área de origem no Entra ID; vazio quando o serviço não informa. */
  time: string;
  /** Vazio quando `papel_id` é nulo — a tela sinaliza "Sem perfil". */
  papelId: string;
  papel: string;
  status: UserStatus;
  /** URL da foto do Entra ID; vazia quando não há imagem. */
  fotoUrl: string;
  /** Data/hora ISO da última atividade; vazia quando não há registro. */
  ultimaAtividade: string;
}

export interface UserFilterOption {
  label: string;
  value: string;
}

/** Iniciais do avatar quando o usuário não tem foto: primeiro e último nome. */
export function getUserInitials(nome: string): string {
  const parts = nome.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";

  const first = parts[0]?.charAt(0) ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? "") : "";

  return `${first}${last}`.toUpperCase();
}

/**
 * Telefone no formato do protótipo (+55 24 99900-0000). Um número fora do padrão
 * brasileiro é exibido como veio do serviço, sem tentativa de correção.
 */
export function formatUserPhone(telefone: string): string {
  const value = telefone.trim();
  if (!value) return "";

  // Um prefixo internacional de outro país sai como veio: só o +55 é reescrito.
  if (value.startsWith("+") && !value.startsWith("+55")) return value;

  const digits = value.replace(/\D/g, "");
  const national = digits.length > 11 && digits.startsWith("55") ? digits.slice(2) : digits;
  if (national.length !== 10 && national.length !== 11) return value;

  const ddd = national.slice(0, 2);
  const line = national.slice(2);

  return `+55 ${ddd} ${line.slice(0, line.length - 4)}-${line.slice(-4)}`;
}

/**
 * Colunas de dado ordenáveis. Os Padrões Globais de Interface preveem ordenação
 * pelo cabeçalho em todas as colunas do grid; Foto e Ações não carregam dado
 * comparável e por isso ficam de fora.
 */
export type UsersSortField = "nome" | "email" | "time" | "papel" | "status" | "ultimaAtividade";

export type UsersSortDirection = "asc" | "desc";

/** Ordenação enviada ao serviço no formato `campo_direcao`. O padrão é Nome (A–Z). */
export type UsersSort = `${UsersSortField}_${UsersSortDirection}`;

export const USERS_SORT_FIELDS: UsersSortField[] = [
  "nome",
  "email",
  "time",
  "papel",
  "status",
  "ultimaAtividade",
];

export const DEFAULT_USERS_SORT: UsersSort = "nome_asc";

export function buildUsersSort(field: UsersSortField, direction: UsersSortDirection): UsersSort {
  return `${field}_${direction}`;
}

/** Leitura tolerante: um valor desconhecido cai no padrão em vez de quebrar a tela. */
export function parseUsersSort(sort: string): {
  field: UsersSortField;
  direction: UsersSortDirection;
} {
  const separator = sort.lastIndexOf("_");
  const field = sort.slice(0, separator) as UsersSortField;
  const direction = sort.slice(separator + 1) as UsersSortDirection;

  if (!USERS_SORT_FIELDS.includes(field) || (direction !== "asc" && direction !== "desc")) {
    return { field: "nome", direction: "asc" };
  }

  return { field, direction };
}

/** Valores aceitos pelo serviço no corpo de `PATCH .../status`. */
export const USER_STATUS_PAYLOAD: Record<UserStatus, string> = {
  ativo: "Ativo",
  inativo: "Inativo",
};

/** Rótulo do status na tela: a listagem e os modais escrevem Ativo ou Inativo. */
export const USER_STATUS_LABEL: Record<UserStatus, string> = {
  ativo: "Ativo",
  inativo: "Inativo",
};

export interface ListUsersParams {
  pesquisa?: string;
  time?: string;
  papelId?: string;
  page?: number;
  pageSize?: number;
  ordenacao?: UsersSort;
  signal?: AbortSignal;
}

/** RN04: a alteração de status parte da confirmação do administrador no modal. */
export interface UpdateUserStatusParams {
  id: string;
  status: UserStatus;
  signal?: AbortSignal;
}

/** Vínculo de perfil do usuário: o par que a listagem exibe na coluna Perfil. */
export interface UserRole {
  papelId: string;
  papel: string;
}

/** RN01/RN02: a operação só carrega o perfil; a identidade é imutável (RN03). */
export interface UpdateUserRoleParams {
  id: string;
  papelId: string;
  signal?: AbortSignal;
}

export interface UsersPage {
  users: User[];
  /** Total de registros que atendem aos critérios, e não apenas os da página. */
  total: number;
  /** Quantidade de usuários sem perfil definido, sinalizada no cabeçalho da coluna Perfil. */
  semPapel: number;
  times: UserFilterOption[];
  papeis: UserFilterOption[];
}

export type UsersStatus = "loading" | "success" | "error" | "forbidden";

export interface UsersQueryState {
  status: UsersStatus;
  users: User[];
  total: number;
  semPapel: number;
  errorMessage: string | null;
}
